import { TextInputWithLabel } from '@/src/common/components/elements/Forms/TextInputWithLabel'
import {
    BaseSyntheticEvent,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react'
import { debounce, trimStart } from 'lodash'
import {
    poBoxRegex,
    shippingNameMax,
} from '@/src/modules/build-a-card/constants'
import { FormErrorDisplay } from '@/src/modules/build-a-card/FormErrorDisplay'
import {
    addressEntriesData,
    clearAddressSearchValue,
    clearAddressSuggestions,
    clearSelectedAddress,
    formattedAddressInList,
    handleSuggestionSelect,
    IHandleSelectFunctions,
} from '@/src/modules/smarty/utilities'
import {
    AddressDisplayWrapper,
    AddressSuggestionListItem,
    AddressSuggestionsList,
    FieldWrapper,
    formattedAddressDisplay,
    SelectError,
} from '@/src/modules/smarty/StyledComponents'
import { smarty } from '@/src/modules/smarty/Smarty'
import { styled } from '../../stitches.config'
import { useRecipientDeliveryContext } from '@/src/common/contexts/RecipientDeliveryContext'
import { inputHasError } from '@/src/common/utilities/inputHasError'
import { clearFormErrorForField } from '@/src/common/utilities/clearFormErrorForField'
import handleEnterPressEvent from '@/src/common/utilities/handleEnterPressEvent'

const ClickSpan = styled('a', {
    textDecoration: 'underline',
    color: '$secondary',
    fontWeight: 'bold',
    cursor: 'pointer',
})

const ManualOption = styled('div', {
    margin: 10,
    cursor: 'pointer',
    color: '$secondary',
    textDecoration: 'underline',
})

interface ISmartyAddressSuggestion {
    streetLine: string
    secondary: string
    city: string
    state: string
    zipcode: string
    entries: number
}

export const SmartyAddressFields = () => {
    const {
        name,
        address1,
        setName,
        addressPayload,
        smartyFunctions,
        formErrors,
        setFormErrors,
        setSearchManually,
    } = useRecipientDeliveryContext()
    const [addressSuggestions, setAddressSuggestions] = useState([])
    const [addressSearchValue, setAddressSearchValue] = useState('')
    const [searchError, setSearchError] = useState('')

    const handleSelectFunctions: IHandleSelectFunctions = {
        setAddressSuggestions: setAddressSuggestions,
        setAddressSearchValue: setAddressSearchValue,
        smartyFunctions: smartyFunctions,
    }

    const clearAllAddressSearch = () => {
        clearAddressSearchValue(setAddressSearchValue)
        clearAddressSuggestions(setAddressSuggestions)
        clearSelectedAddress(smartyFunctions)
    }

    const changeHandler = useCallback(
        async (event: BaseSyntheticEvent | Event) => {
            const suggestions = await smarty({
                street: event.target.value,
            })

            const suggestionsWithoutPOBox = suggestions?.filter(
                (item: any) => !item.streetLine.includes('PO Box')
            )

            setAddressSuggestions(suggestionsWithoutPOBox ?? [])
        },
        [setAddressSuggestions]
    )
    const debouncedChangeHandler = useMemo(() => {
        return debounce(changeHandler, 300)
    }, [changeHandler])

    useEffect(() => {
        if (poBoxRegex.test(addressSearchValue)) {
            setSearchError(
                'For security purposes, we are unable to ship to P.O. Boxes. We apologize for the inconvenience.'
            )
        } else {
            setSearchError('')
        }
    }, [addressSearchValue])

    return (
        <>
            <FieldWrapper>
                <TextInputWithLabel
                    label={'Full Name'}
                    value={name}
                    onChange={(e: BaseSyntheticEvent) => {
                        const trimmedValue = trimStart(e.currentTarget.value)
                        setName(trimmedValue)
                        clearFormErrorForField({
                            path: 'name',
                            formErrors: formErrors,
                            setFormErrors: setFormErrors,
                        })
                    }}
                    maxLength={shippingNameMax}
                    hasError={inputHasError(formErrors, 'name')}
                    data-testid={'name'}
                />
                <FormErrorDisplay formErrors={formErrors} path={'name'} />
            </FieldWrapper>
            <FieldWrapper style={{ position: 'relative' }}>
                {!address1 && (
                    <>
                        <TextInputWithLabel
                            label={'Delivery Address'}
                            placeholder={'Search'}
                            value={addressSearchValue}
                            onChange={(e: any) => {
                                setAddressSearchValue(e.target.value)
                                debouncedChangeHandler(e)
                                clearFormErrorForField({
                                    path: 'address1',
                                    formErrors: formErrors,
                                    setFormErrors: setFormErrors,
                                })
                            }}
                            hasError={inputHasError(formErrors, 'address1')}
                            data-testid={'address-1'}
                        />
                        {searchError && (
                            <SelectError>{searchError}</SelectError>
                        )}
                        {Boolean(addressSuggestions.length) && (
                            <AddressSuggestionsList>
                                {addressSuggestions.map(
                                    (
                                        addressSuggestion: ISmartyAddressSuggestion,
                                        index
                                    ) => (
                                        <AddressSuggestionListItem
                                            key={`addrsugg_${index}`}
                                            data-address-entries-refinement-data={addressEntriesData(
                                                addressSuggestion
                                            )}
                                            onClick={(e) => {
                                                handleSuggestionSelect(
                                                    e,
                                                    addressSuggestion,
                                                    addressSearchValue,
                                                    handleSelectFunctions
                                                ).then((r) => {})
                                            }}
                                            data-testid={`address-suggestion-${index}`}
                                        >
                                            {formattedAddressInList(
                                                addressSuggestion
                                            )}
                                        </AddressSuggestionListItem>
                                    )
                                )}
                                <ManualOption
                                    onClick={() => {
                                        setSearchManually(true)
                                    }}
                                >
                                    Didn&#39;t find the right address? Enter it
                                    manually!
                                </ManualOption>
                            </AddressSuggestionsList>
                        )}
                        <FormErrorDisplay
                            formErrors={formErrors}
                            path={'address1'}
                        />
                    </>
                )}
                {address1 && (
                    <AddressDisplayWrapper>
                        <p>Selected Delivery Address:</p>
                        <span data-testid={'formatted-address'}>
                            {formattedAddressDisplay(addressPayload)}
                        </span>
                    </AddressDisplayWrapper>
                )}
            </FieldWrapper>
            <p style={{ marginBottom: 5 }}>
                Didn&#39;t find the right address?
            </p>
            <p style={{ marginTop: 0 }}>
                {address1 && (
                    <>
                        <ClickSpan
                            data-testid={'search-again'}
                            onClick={clearAllAddressSearch}
                            onKeyDown={(e) =>
                                handleEnterPressEvent(e, () =>
                                    clearAllAddressSearch()
                                )
                            }
                            tabIndex={0}
                        >
                            Search again
                        </ClickSpan>{' '}
                        or{' '}
                    </>
                )}
                <ClickSpan
                    data-testid={'enter-manually'}
                    onClick={() => setSearchManually(true)}
                    onKeyDown={(e) =>
                        handleEnterPressEvent(e, () => setSearchManually(true))
                    }
                    tabIndex={0}
                >
                    Enter it manually
                </ClickSpan>
            </p>
        </>
    )
}
